import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ErrorDeApi } from '@nucleo/api/cliente'
import { Boton } from '@ui/primitivas/Boton'
import { Campo } from '@ui/primitivas/Campo'
import { confirmarMatriz, descargarExportacion, obtenerMatriz } from './clienteDeMatrices'
import { Subpaso } from './ComponentesDeEtapa'
import { CLASES_DE_NIVEL, nombreDeArchivoDeExportacion } from './etiquetas'
import { VistaPreviaMiper } from './VistaPreviaMiper'
import type { Matriz, NivelDeRiesgo } from './tipos'

const NIVELES: NivelDeRiesgo[] = ['intolerable', 'importante', 'moderado', 'tolerable']

export function PasoOficializar(props: { matriz: Matriz; alActualizar: (m: Matriz) => void }) {
  const navegar = useNavigate()
  const { matriz } = props
  const [confirmadoPor, setConfirmadoPor] = useState(matriz.alcance_detalle.responsable.nombre)
  const [declaro, setDeclaro] = useState(false)
  const [trabajando, setTrabajando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exito, setExito] = useState<string | null>(null)
  const [descargando, setDescargando] = useState(false)
  const [vistaLista, setVistaLista] = useState<number | null>(null)

  const validadas = matriz.filas.filter((f) => f.estado === 'validada')
  const porNivel = NIVELES.map((n) => ({ nivel: n, cantidad: validadas.filter((f) => f.version_motor === 'evaluacion-isp-v3' && f.evaluacion.tipo === 'motor' && f.evaluacion.nivel === n).length }))
  const porProtocolo = validadas.filter((f) => f.evaluacion.tipo === 'protocolo')
  const pendientesDeProtocolo = porProtocolo.filter((f) => f.evaluacion.tipo === 'protocolo' && !f.evaluacion.consolidada)
  const bloqueos = matriz.bloqueos_de_confirmacion
  const oficial = matriz.estado === 'oficial'
  const ultimaRevision = matriz.revisiones.at(-1)
  const hayRevisionOficial = ultimaRevision !== undefined
  const exportable = ultimaRevision
    ? ultimaRevision.version_motor === 'evaluacion-isp-v3'
    : matriz.filas.filter((f) => f.estado !== 'descartada').every((f) => f.version_motor === 'evaluacion-isp-v3')

  const confirmar = async () => {
    setTrabajando(true)
    setError(null)
    setExito(null)
    try {
      const r = await confirmarMatriz(matriz.id, confirmadoPor.trim())
      props.alActualizar(await obtenerMatriz(matriz.id))
      setExito(`Revisión ${r.numero} oficializada con ${r.filas} fila(s).`)
      setDeclaro(false)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No fue posible oficializar la matriz.')
    } finally {
      setTrabajando(false)
    }
  }

  const descargar = async () => {
    setDescargando(true)
    setError(null)
    try {
      await descargarExportacion(matriz.id, nombreDeArchivoDeExportacion(matriz.alcance, matriz.revision_actual, hayRevisionOficial))
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No fue posible descargar la matriz.')
    } finally {
      setDescargando(false)
    }
  }

  return (
    <div className="grid gap-4">
      <div>
        <p className="cs-eyebrow">Etapa 4 · Oficializar y exportar</p>
        <h2 className="text-h2">Confirma la matriz y descárgala en formato ISP</h2>
      </div>

      <section className="sst-tarjeta grid gap-3 p-4 sm:p-5" aria-labelledby="titulo-resumen-oficial">
        <h3 id="titulo-resumen-oficial" className="text-h3">Resumen de lo que se oficializa</h3>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sst-indicador rounded-lg bg-app"><dt>Tareas validadas</dt><dd><strong>{matriz.progreso.tareas_validadas}</strong></dd></div>
          <div className="sst-indicador rounded-lg bg-app"><dt>Filas validadas</dt><dd><strong>{matriz.progreso.filas_validadas}</strong></dd></div>
          <div className="sst-indicador rounded-lg bg-app"><dt>Por protocolo</dt><dd><strong>{porProtocolo.length}</strong> <small>{pendientesDeProtocolo.length} pendientes</small></dd></div>
          <div className="sst-indicador rounded-lg bg-app"><dt>Revisión actual</dt><dd><strong>{matriz.revision_actual || '—'}</strong></dd></div>
        </dl>
        <ul className="flex flex-wrap gap-2" aria-label="Filas por nivel de riesgo">
          {porNivel.map((n) => (
            <li key={n.nivel} className={`sst-nivel ${CLASES_DE_NIVEL[n.nivel]}`}>
              {n.cantidad} {n.nivel}
            </li>
          ))}
        </ul>
        {pendientesDeProtocolo.length ? (
          <p className="text-pequeno text-texto-secundario">
            {pendientesDeProtocolo.length} fila(s) higiénicas, psicosociales o músculo-esqueléticas quedan «pendientes de protocolo»: se exportan así, sin inventar mediciones, y podrás consolidarlas en una revisión posterior.
          </p>
        ) : null}
      </section>

      <Subpaso numero="4.1" titulo="Confirma la matriz como oficial" detalle="Queda registrado quién la confirma y cuándo; cada fila conserva su base y su visto bueno." activo={!oficial && bloqueos.length === 0}>
        {bloqueos.length ? (
          <div role="status" className="rounded-md bg-atencion-fondo p-3 text-atencion-texto">
            <p className="font-semibold">Antes de confirmar:</p>
            <ul className="list-disc pl-5 text-pequeno">
              {bloqueos.map((b) => (
                <li key={b.codigo}>{b.mensaje}</li>
              ))}
            </ul>
            <div className="mt-2 flex flex-wrap gap-2">
              <Boton variante="secundario" compacto alPulsar={() => navegar(`/matrices/${matriz.id}/levantamiento`)}>Ir al levantamiento</Boton>
              <Boton variante="secundario" compacto alPulsar={() => navegar(`/matrices/${matriz.id}/matriz`)}>Ir a la matriz</Boton>
            </div>
          </div>
        ) : oficial ? (
          <p role="status" className="rounded-md bg-conforme-fondo p-3 text-conforme-texto">
            Matriz oficial · revisión {matriz.revision_actual}, confirmada por {matriz.revisiones.at(-1)?.confirmada_por} el {new Date(matriz.revisiones.at(-1)?.confirmada_en ?? '').toLocaleString('es-CL')}. Cualquier cambio abrirá el borrador de la revisión {matriz.revision_actual + 1}.
          </p>
        ) : (
          <form
            className="grid gap-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (declaro && confirmadoPor.trim()) void confirmar()
            }}
          >
            <Campo etiqueta="Confirmada por" valor={confirmadoPor} alCambiar={setConfirmadoPor} requerido ayuda="Nombre de la persona que oficializa la matriz (queda en la trazabilidad)." />
            <label className="flex items-start gap-3 rounded-lg border border-borde bg-app p-3 text-cuerpo">
              <input type="checkbox" checked={declaro} onChange={(e) => setDeclaro(e.target.checked)} className="mt-1 h-5 w-5 flex-none" />
              <span>Declaro que revisé cada tarea y cada fila validada, y que las medidas propuestas por Atalaya fueron verificadas por mí o por el equipo participante.</span>
            </label>
            <div>
              {!declaro || !confirmadoPor.trim() ? <p id="requisito-declaracion" className="mb-2 text-pequeno text-texto-secundario">Para confirmar, escribe quién oficializa y marca la declaración.</p> : null}
              <Boton tipo="submit" trabajando={trabajando} deshabilitado={!declaro || !confirmadoPor.trim()} {...(!declaro || !confirmadoPor.trim() ? { describidoPor: 'requisito-declaracion' } : {})}>
                Confirmar matriz como oficial (revisión {matriz.revision_actual + 1})
              </Boton>
            </div>
          </form>
        )}
        {exito ? <p role="status" className="rounded-md bg-conforme-fondo p-3 text-conforme-texto">{exito}</p> : null}
        {error ? <p role="alert" className="rounded-md bg-critico-fondo p-3 text-critico-texto">{error}</p> : null}
      </Subpaso>

      {ultimaRevision ? <VistaPreviaMiper matrizId={matriz.id} revision={ultimaRevision.numero} alCargar={setVistaLista} /> : null}

      <Subpaso numero={hayRevisionOficial ? '4.3' : '4.2'} titulo="Exporta la MIPER" activo={oficial}>
        <p className="text-pequeno text-texto-secundario">
          Libro Excel basado en el Anexo 6 de la guía ISP (hoja MIPER), con tablas del motor, participantes y trazabilidad por fila.
          {hayRevisionOficial ? ` Se exporta el snapshot oficial de la revisión ${ultimaRevision.numero}, incluso si ya existe un borrador posterior.` : ' Mientras no esté oficializada, el archivo se marca como borrador.'}
        </p>
        {!exportable ? <p role="status" className="text-pequeno text-atencion-texto">La revisión disponible usa una evaluación anterior. Para descargar en formato ISP, reevalúa las filas y confirma una nueva revisión.</p> : null}
        {hayRevisionOficial && vistaLista !== ultimaRevision.numero ? <p role="status" className="text-pequeno text-atencion-texto">La descarga se habilita cuando la vista previa oficial esté disponible.</p> : null}
        <div className="flex flex-wrap gap-2">
          <Boton alPulsar={() => void descargar()} trabajando={descargando} deshabilitado={!exportable || (hayRevisionOficial && vistaLista !== ultimaRevision.numero)} variante={hayRevisionOficial ? 'primario' : 'secundario'}>
            {hayRevisionOficial ? 'Descargar MIPER oficial (.xlsx)' : 'Descargar borrador (.xlsx)'}
          </Boton>
          <Boton variante="secundario" alPulsar={() => navegar('/')}>Volver a las matrices</Boton>
        </div>
      </Subpaso>

      {matriz.revisiones.length ? (
        <section className="sst-tarjeta grid gap-2 p-4 sm:p-5" aria-labelledby="titulo-revisiones">
          <h3 id="titulo-revisiones" className="text-h3">Revisiones oficiales</h3>
          <ol className="grid gap-1 text-pequeno">
            {matriz.revisiones.map((r) => (
              <li key={r.numero}>
                Revisión {r.numero} · {r.filas} fila(s) · {r.confirmada_por} · {new Date(r.confirmada_en).toLocaleString('es-CL')}
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </div>
  )
}
